import type { HTMLAttributes, ReactNode } from "react";

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  padding?: "none" | "sm" | "md";
  /** Gentle lift on hover — only for cards that are themselves navigational. */
  hover?: boolean;
}

/**
 * The standard surface: 18px radius, a softened border, and the tight+ambient
 * two-layer shadow. White on the near-white canvas — the contrast is deliberately
 * subtle, so depth comes from the shadow rather than from a colour step.
 */
export function Card({ children, className = "", padding = "md", hover = false, ...props }: CardProps) {
  const paddingClass = padding === "none" ? "" : padding === "sm" ? "p-4 sm:p-5" : "p-5 sm:p-6";

  return (
    <div
      className={`rounded-xl border border-line/80 bg-card shadow-sm ${hover ? "card-hover" : ""} ${paddingClass} ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * Separate from Card rather than a prop, because plenty of cards hold a table or
 * an empty state and need no header at all.
 *
 * `action` holds secondary controls (Preview, Reset) — never the form's primary
 * submit, which belongs at the bottom of the form it submits.
 */
export function CardHeader({
  title,
  description,
  action,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="mb-5 flex items-start justify-between gap-3">
      <div>
        <h2 className="text-[0.9375rem] font-semibold tracking-tight text-ink-900">{title}</h2>
        {description && <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{description}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}
